import "pages/StandardTreatment/shared/masterData.scss";
import "pages/MasterPengobatanStandar/index.scss";
import "./index.scss";
import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { FiArrowLeft, FiPlus } from "react-icons/fi";
import Button from "components/atoms/Button";
import ActionButton from "components/atoms/ActionButton";
import TableDataComponent from "components/organisms/TableData";
import standardTreatmentService, { errorMessages } from "services/StandardTreatment";
import { formatBandDose, formatBandRange } from "pages/MasterPengobatanStandar/constants";
import useServiceData from "../shared/useServiceData";
import useBootstrapModal from "../shared/useBootstrapModal";
import { ErrorState } from "../shared/ui";
import DoseBandFormModal, { DOSE_BAND_MODAL_ID } from "./DoseBandFormModal";

// Dosis standar selalu milik satu aturan dosis: Drug → Regimen → Dose Band.
const MasterDoseBand = () => {
  const regimenId = new URLSearchParams(useLocation().search).get("regimen_id");
  const { modal, open, close } = useBootstrapModal(DOSE_BAND_MODAL_ID);

  useEffect(() => {
    document.title = "SISMAL | Dosis Standar";
  }, []);

  const regimens = useServiceData(() => standardTreatmentService.listRegimens({ page: 1, limit: 100 }), []);
  const regimenList = regimens.data?.regimens || [];
  const selected = regimenList.find((r) => r.id === regimenId);

  const bands = useServiceData(
    () =>
      regimenId
        ? standardTreatmentService.listDoseBands({ regimen_id: regimenId, page: 1, limit: 100 })
        : Promise.resolve(null),
    [regimenId]
  );

  const removeBand = (band) =>
    confirmAlert({
      title: "Hapus dosis standar?",
      message: `${formatBandRange(band)} → ${formatBandDose(band)}. Dosis standar dihapus permanen (tercatat di audit trail).`,
      buttons: [
        {
          label: "Ya, hapus",
          onClick: () =>
            standardTreatmentService
              .deleteDoseBand(band.id)
              .then(() => {
                toast.success("Dosis standar dihapus");
                bands.reload();
              })
              .catch((err) => toast.error(errorMessages(err).join(", "))),
        },
        { label: "Batal" },
      ],
    });

  const columns = useMemo(
    () => [
      { header: "Rentang Berat", size: 170, cell: ({ row }) => <code className="md-code">{formatBandRange(row.original)}</code> },
      { header: "Dosis", size: 200, cell: ({ row }) => formatBandDose(row.original) },
      {
        header: "Bentuk",
        size: 140,
        cell: ({ row }) =>
          row.original.dose_amount !== null ? "Dosis tepat" : `Rentang ${Number(row.original.dose_amount_min)} – ${Number(row.original.dose_amount_max)}`,
      },
      { header: "Satuan", accessorKey: "dose_unit", size: 90 },
      {
        header: "Aksi",
        size: 150,
        cell: ({ row }) => (
          <div className="row-actions">
            <ActionButton btnType="edit" onClick={() => open("edit", row.original)} />
            <ActionButton btnType="delete" onClick={() => removeBand(row.original)} />
          </div>
        ),
      },
    ],
    [bands.reload]
  );

  const rows = bands.data?.regimen_dose_bands || [];
  const tableInstance = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel() });

  const saveBand = (body) =>
    (modal.mode === "edit"
      ? standardTreatmentService.updateDoseBand(modal.item.id, body)
      : standardTreatmentService.createDoseBand({ regimen_id: regimenId, ...body })
    ).then(() => {
      close();
      toast.success("Dosis standar disimpan");
      bands.reload();
    });

  return (
    <div className="master-data-page">
      <Link to="/master-obat" className="page-back">
        <FiArrowLeft size={16} className="mr-2" />
        Kembali ke Master Obat
      </Link>
      <h1 className="page-title">Dosis Standar</h1>
      <p className="page-subtitle">
        Dosis standar = rentang berat badan dan dosis yang diharapkan di dalamnya. Selalu melekat pada
        satu aturan dosis; dosis dalam aturan dosis yang sama tidak boleh tumpang tindih.
      </p>

      {regimens.error ? (
        <ErrorState messages={regimens.error} onRetry={regimens.reload} />
      ) : (
        <section className="dose-band-content">
          {selected ? (
            <>
              <div className="action-wrapper">
                <div>
                  <p className="mps-muted mb-1">
                    {selected.drug?.name} › <code className="md-code">{selected.code}</code>
                  </p>
                  <h2 className="md-section-title mt-0">{selected.name}</h2>
                </div>
                <Button variant="primary" size="normal" onClick={() => open("add")}>
                  <FiPlus size={16} className="mr-2" />
                  Tambah Dosis Standar
                </Button>
              </div>
              {!selected.is_active && (
                <p className="md-warning mt-3">Aturan dosis ini nonaktif dan tidak ditawarkan pada Terapi.</p>
              )}
              {bands.error ? (
                <div className="mt-3">
                  <ErrorState messages={bands.error} onRetry={bands.reload} />
                </div>
              ) : !bands.loading && !rows.length ? (
                <div className="mps-empty mt-4">
                  Aturan dosis ini belum punya dosis standar. Versi yang memakainya tanpa dosis standar
                  ditolak saat rilis (REGIMEN_HAS_DOSE_BAND).
                </div>
              ) : (
                <TableDataComponent
                  columnLength={columns.length}
                  tableInstance={tableInstance}
                  onPageChange={() => {}}
                  loading={bands.loading}
                  currentPage={1}
                  limit={bands.data?.limit ?? 100}
                  total={bands.data?.total ?? 0}
                />
              )}
            </>
          ) : (
            !regimens.loading && regimenId && <div className="mps-empty">Aturan dosis tidak ditemukan.</div>
          )}
        </section>
      )}

      <DoseBandFormModal
        key={modal.openCount}
        mode={modal.mode}
        band={modal.item}
        regimen={selected}
        onSubmit={saveBand}
      />
    </div>
  );
};

export default MasterDoseBand;
